import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Chip, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import { fmtDateTime, fmtMoney, fmtNumber, plural } from '../format';
import { CardItem, DocumentCard, DocumentsAccess, ItemSeries } from '../types';
import { BORDER, DANGER, DARK, HEAD_CELL, PRIMARY, SOFT, SUCCESS } from './cardKit';

/** Статусы приёмки: расхождение факта с планом имеет смысл подсвечивать только в них */
const ACCEPTED_STATUSES = [3, 4, 10, 16];
/** Сколько серий товара показывается прямо в строке - остальные уходят в подсказку «+ ещё N серий» */
const INLINE_SERIES = 2;

/** Серии позиции как в /dashboard: первые две в строке, остальные - по наведению на «+ ещё N серий» */
const SeriesInline: React.FC<{ series: ItemSeries[] }> = ({ series }) => {
  if (!series.length) return null;
  const rest = series.slice(INLINE_SERIES);
  return (
    <>
      {series.slice(0, INLINE_SERIES).map(s => (
        <Typography
          key={`${s.number}__${s.date}`} variant="caption" title={s.certificate ? `Сертификат: ${s.certificate}` : undefined}
          sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
        >
          <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>•</Box>
          <Box component="span" sx={{ mr: 0.5 }}>Серия:</Box>
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>{s.number}</Box>
          {s.date && <Box component="span" sx={{ ml: 0.5 }}>(до {s.date})</Box>}
          {s.count > 1 && <Box component="span" sx={{ color: PRIMARY, fontWeight: 600, ml: 0.5 }}>[{s.count} шт]</Box>}
        </Typography>
      ))}
      {rest.length > 0 && (
        <Tooltip
          arrow placement="top"
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Остальные серии товара:</Typography>
              {rest.map(s => (
                <Typography key={`${s.number}__${s.date}`} variant="caption" sx={{ display: 'block', fontSize: '0.72rem', lineHeight: 1.4 }}>
                  • <strong>{s.number}</strong> {s.date ? `(до ${s.date})` : ''} {s.count > 1 ? `- ${s.count} шт.` : ''}
                </Typography>
              ))}
            </Box>
          }
        >
          <Typography variant="caption" sx={{ display: 'inline-flex', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>•</Box>
            <Box component="span" sx={{ color: PRIMARY, fontWeight: 600, textDecoration: 'underline dotted' }}>
              + ещё {rest.length} {plural(rest.length, 'серия', 'серии', 'серий')}
            </Box>
          </Typography>
        </Tooltip>
      )}
    </>
  );
};

/** Серии позиции: из ZF_tbl_series, а если там пусто - одиночный код серии из самой позиции */
const itemSeries = (it: CardItem): ItemSeries[] => {
  if (it.series.length) return it.series;
  return it.serialCode ? [{ number: it.serialCode, date: '', certificate: '', count: 1 }] : [];
};

const qtyLabel = (packs: number, pcs: number): string => {
  const parts: string[] = [];
  if (packs > 0) parts.push(`${fmtNumber(packs)} уп`);
  if (pcs > 0) parts.push(`${fmtNumber(pcs)} шт`);
  return parts.length ? parts.join(' + ') : '-';
};

interface ItemRowProps {
  item: CardItem;
  index: number;
  status: number;
  access: DocumentsAccess;
  showPrices: boolean;
  has1C: boolean;
  hasStatusColumn: boolean;
}

const ItemRow = React.memo(function ItemRow({ item, index, status, access, showPrices, has1C, hasStatusColumn }: ItemRowProps) {
  const series = itemSeries(item);
  const diff = ACCEPTED_STATUSES.includes(status)
    && (item.quantityFact !== item.quantity || item.quantityFactPart !== item.quantityPcs);
  const hasError = Boolean(item.processStatus) && item.processStatus !== 'Проведен';

  return (
    <TableRow hover sx={{ '&:last-child td': { border: 0 }, '& td': { verticalAlign: 'top' } }}>
      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: 'monospace' }}>{index + 1}</TableCell>
      <TableCell sx={{ fontSize: '0.85rem' }}>
        <Typography variant="body2" sx={{ color: DARK, fontWeight: 500 }}>{item.materialName || '-'}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ШК: {item.barcode || '-'} • Код: {item.materialCode || '-'}
          </Typography>
          {access.viewSeries && <SeriesInline series={series} />}
        </Box>
      </TableCell>
      <TableCell>
        {item.marksCount > 0 ? (
          <Tooltip
            arrow placement="right"
            title={access.viewMarks && item.markCodes.length > 0
              ? (
                <Box>
                  {item.markCodes.slice(0, 12).map(m => <div key={m.id} style={{ fontFamily: 'monospace', fontSize: 11 }}>{m.mark}</div>)}
                  {item.markCodes.length > 12 && <div>…ещё {item.markCodes.length - 12}</div>}
                </Box>
              )
              : ''}
          >
            <Chip
              icon={<QrCode2RoundedIcon style={{ fontSize: 12 }} />} label={`${item.marksCount} шт`} size="small" variant="outlined"
              sx={{ height: 22, fontSize: '0.75rem', borderColor: PRIMARY, color: PRIMARY, bgcolor: alpha(PRIMARY, 0.05) }}
            />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
        {qtyLabel(item.quantity, item.quantityPcs)}
        {diff && (
          <Typography variant="caption" component="div" sx={{ color: DANGER, fontWeight: 600 }}>
            факт: {qtyLabel(item.quantityFact, item.quantityFactPart)}
          </Typography>
        )}
      </TableCell>
      {has1C && (
        <TableCell sx={{ fontSize: '0.8rem', color: item.quantity1C ? 'text.primary' : 'text.disabled', fontFamily: item.quantity1C ? 'monospace' : 'inherit' }}>
          {item.quantity1C ? fmtNumber(item.quantity1C) : '-'}
        </TableCell>
      )}
      {showPrices && (
        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {item.stockPrice ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: DARK, lineHeight: 1.2 }}>
                {item.lineTotal ? fmtMoney(item.lineTotal) : '-'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{fmtMoney(item.stockPrice, 2)}/уп</Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled">-</Typography>
          )}
        </TableCell>
      )}
      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{fmtDateTime(item.updateDate)}</TableCell>
      {hasStatusColumn && (
        <TableCell>
          {hasError && (
            <Typography variant="caption" sx={{
              color: DANGER, fontWeight: 600, display: 'block', lineHeight: 1.2, bgcolor: alpha(DANGER, 0.05),
              p: 1, borderRadius: 1, border: `1px solid ${alpha(DANGER, 0.2)}`
            }}>
              {item.processStatus}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
});

interface Props {
  doc: DocumentCard;
  access: DocumentsAccess;
  pricesAvailable: boolean;
}

/** Правая часть карточки: товары документа с поиском, суммой и ошибками проведения */
const CardItemsPanel: React.FC<Props> = ({ doc, access, pricesAvailable }) => {
  const [itemSearch, setItemSearch] = useState('');

  const showPrices = doc.items.some(i => i.basePrice !== undefined);
  const hasStatusColumn = doc.items.some(i => i.processStatus && i.processStatus !== 'Проведен');
  const has1C = doc.items.some(i => i.quantity1C !== null);
  const hasTotal = doc.totalCost !== undefined;

  const items = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return doc.items;
    return doc.items.filter(i =>
      i.materialName.toLowerCase().includes(q) || i.materialCode.toLowerCase().includes(q) || i.barcode.toLowerCase().includes(q) ||
      (access.viewSeries && itemSeries(i).some(s => s.number.toLowerCase().includes(q)))
    );
  }, [doc, itemSearch, access.viewSeries]);

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: SOFT, overflow: { xs: 'visible', md: 'hidden' } }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DARK }}>Товары ({doc.items.length})</Typography>
        {hasTotal && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: alpha(SUCCESS, 0.08), border: `1px solid ${alpha(SUCCESS, 0.25)}`, borderRadius: 1.5, px: 1.5, py: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>ИТОГО:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: SUCCESS }}>
              {doc.totalCost === null || doc.totalCost === undefined ? '-' : fmtMoney(doc.totalCost)}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small" placeholder={access.viewSeries ? 'Поиск по товару, коду, серии' : 'Поиск по товару, коду, штрихкоду'}
          value={itemSearch} onChange={e => setItemSearch(e.target.value)} sx={{ width: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment> }}
        />
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {(access.docAmount || access.itemAmounts) && !pricesAvailable && (
          <Alert severity="info" sx={{ py: 0 }}>Цены сейчас недоступны (сервис номенклатуры не отвечает) - суммы не рассчитаны.</Alert>
        )}
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL}>№</TableCell>
                  <TableCell sx={HEAD_CELL}>НАИМЕНОВАНИЕ</TableCell>
                  <TableCell sx={{ ...HEAD_CELL, width: 100 }}>КОДЫ</TableCell>
                  <TableCell sx={{ ...HEAD_CELL, width: 130 }}>КОЛ-ВО</TableCell>
                  {has1C && <TableCell sx={{ ...HEAD_CELL, width: 80 }}>1С</TableCell>}
                  {showPrices && <TableCell sx={{ ...HEAD_CELL, width: 120 }}>ЦЕНА</TableCell>}
                  <TableCell sx={{ ...HEAD_CELL, width: 140 }}>ОБНОВЛЕН</TableCell>
                  {hasStatusColumn && <TableCell sx={{ ...HEAD_CELL, width: 250 }}>СТАТУС (ОШИБКИ)</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it, index) => (
                  <ItemRow
                    key={it.id} item={it} index={index} status={doc.status} access={access}
                    showPrices={showPrices} has1C={has1C} hasStatusColumn={hasStatusColumn}
                  />
                ))}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#64748b' }}>
                      {itemSearch ? 'Ничего не найдено' : 'В документе нет позиций'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default React.memo(CardItemsPanel);
